import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { canonicalizeSummaryForDirtyCheck } from '@/modules/documents/documents.utils';

/**
 * Tracks whether a slide editor field has in-progress (uncommitted) changes.
 *
 * The title and rich-text editors only commit their value to the form on blur,
 * so the form's own dirty state lags behind while the user is still typing —
 * navigating away mid-edit (browser back/forward, window close) would otherwise
 * slip past the unsaved-changes blocker. This hook mirrors the editor's live
 * value (fed via the returned `onLiveChange`, e.g. the title editor's `onInput`
 * or the rich text editor's `onRawUpdate`) and compares it against the
 * committed/seed value using the same whitespace-canonicalized diff the editors
 * use for their own dirty checks, so:
 *
 *   - typing flips `isDirty` true immediately (arming the blocker before blur),
 *   - reverting back to the seed flips it false again (no sticky flag), and
 *   - committing on blur updates the seed, settling `isDirty` back to false.
 *
 * `reset()` realigns the live value to the current seed — used as the
 * slide-nav guard's discard handler so abandoning edits clears the dirty state.
 */
export function useSlideFieldLiveDirty(seedValue: string | null | undefined) {
  const normalizedSeed = seedValue ?? '';
  const [liveValue, setLiveValue] = useState(normalizedSeed);

  // Realign the live value whenever the committed seed changes without a
  // keystroke — a blur commit, AI generation, a prompt/content flip, or any
  // other setValue. The dep is the seed's value (a string), so this never fires
  // mid-typing (the seed only changes on commit), and it keeps a stale live
  // value from holding `isDirty` true when the on-screen text matches the form.
  const prevSeedRef = useRef(normalizedSeed);
  useEffect(() => {
    if (prevSeedRef.current !== normalizedSeed) {
      prevSeedRef.current = normalizedSeed;
      setLiveValue(normalizedSeed);
    }
  }, [normalizedSeed]);

  const isDirty = useMemo(
    () =>
      canonicalizeSummaryForDirtyCheck(liveValue) !==
      canonicalizeSummaryForDirtyCheck(normalizedSeed),
    [liveValue, normalizedSeed]
  );

  const reset = useCallback(
    () => setLiveValue(normalizedSeed),
    [normalizedSeed]
  );

  return { onLiveChange: setLiveValue, isDirty, reset };
}
